// Base interface for all database entities
export interface BaseEntity {
  id: string
  createdAt: Date
  updatedAt: Date
  lastModified: Date
  // Sync fields for future Supabase integration
  synced: boolean
  syncedAt: Date | null
}

// Attention levels (how much focus a task needs)
export type AttentionLevel = 'high' | 'medium' | 'low'

// Start period (time-of-day ranges for task scheduling)
export type StartPeriod = 'morning' | 'noon' | 'afternoon' | 'evening'

// Life space — the three top-level areas a task / project / goal belongs to.
export type LifeSpace = 'personal' | 'work' | 'home'

// Task statuses  
export type TaskStatus = 'todo' | 'in_progress' | 'completed' | 'cancelled'

// Task categories
export type TaskCategory = 'work' | 'personal' | 'health' | 'learning' | 'other'

// Task model
export interface Task extends BaseEntity {
  title: string
  description: string | null
  status: TaskStatus
  attention: AttentionLevel | null
  category: TaskCategory
  /**
   * Stable lifespace affinity. Independent of `projectId` so orphan tasks
   * (projectId == null — e.g. unclarified Kurz-notiert entries) still know
   * which of the three lifespaces they belong to.
   */
  lifeSpace: LifeSpace
  projectId: string | null // local project ID (Dexie UUID)
  sectionId: string | null // local section ID (null = no section)
  userId: string | null // owner (Supabase auth user ID)
  /** @deprecated Legacy sync mapping. Use local_id (= task.id) for cross-device identity. */
  cloudId: string | null
  dueDate: Date | null
  dueTime: string | null // "HH:mm" format, mutually exclusive with duePeriod
  duePeriod: StartPeriod | null // mutually exclusive with dueTime
  startDate: Date | null
  startTime: string | null // "HH:mm" format, mutually exclusive with startPeriod
  startPeriod: StartPeriod | null // mutually exclusive with startTime
  completedAt: Date | null
  estimatedDuration: number | null // in minutes
  actualDuration: number | null // in minutes
  durationMode: 'duration' | 'thinking' | null
  tags: string[]
  parentTaskId: string | null // for subtasks
  order: number // for custom ordering
  deletedAt: Date | null // soft delete
  pausedAt: Date | null // set by Clarify-Mode "Ruhen lassen"; null = active, Date = paused since
}

// Section model — groups tasks within a project
export interface Section extends BaseEntity {
  title: string
  projectId: string
  userId: string | null
  /** @deprecated Legacy sync mapping. Use local_id (= section.id) for cross-device identity. */
  cloudId: string | null
  order: number // interleaved with tasks in the same project
  collapsed: boolean
  deletedAt: Date | null
}

// Settings model - key-value store
export interface Setting extends BaseEntity {
  key: string
  value: any // can be string, number, boolean, object
  type: 'string' | 'number' | 'boolean' | 'object' | 'array'
  description: string | null
  isUserSetting: boolean // true for user settings, false for system settings
}

// User data/preferences
export interface UserProfile extends BaseEntity {
  username: string | null
  email: string | null
  fullName: string | null // For offline mode user name
  displayName: string | null
  avatar: string | null
  usagePurposes: string[] // For offline mode: e.g. ['work', 'personal', 'health']
  userIntents: string[] // For offline mode: e.g. ['daily_routines', 'focus']
  preferences: {
    theme: 'light' | 'dark' | 'system'
    language: string
    notifications: boolean
    sounds: boolean
    autoSync: boolean
    workingHours: {
      start: string // HH:mm format
      end: string // HH:mm format
    }
    pomodoroSettings: {
      workDuration: number // minutes
      shortBreakDuration: number // minutes
      longBreakDuration: number // minutes
      cyclesBeforeLongBreak: number
    }
  }
  statistics: {
    totalTasksCompleted: number
    totalTimeSpent: number // minutes
    currentStreak: number // days
    longestStreak: number // days
    lastActiveDate: Date | null
  }
}

// Activity/Time tracking
export interface TimeEntry extends BaseEntity {
  taskId: string
  startTime: Date
  endTime: Date | null // null if still running
  duration: number | null // calculated in minutes
  description: string | null
  isManual: boolean // true if manually added, false if tracked
}

// Tags for organizing tasks
export interface Tag extends BaseEntity {
  name: string
  color: string // hex color code
  description: string | null
  usageCount: number
  userId: string | null
  /** @deprecated Legacy sync mapping. Use local_id (= tag.id) for cross-device identity. */
  cloudId: string | null
  deletedAt: Date | null
}

// Projects to group tasks
export interface Project extends BaseEntity {
  name: string
  description: string | null
  color: string // hex color code
  status: 'active' | 'completed' | 'archived'
  lifeSpace: LifeSpace
  userId: string | null
  /** @deprecated Legacy sync mapping. Use local_id (= project.id) for cross-device identity. */
  cloudId: string | null
  startDate: Date | null
  endDate: Date | null
  progress: number // 0-100 percentage
  order: number
  deletedAt: Date | null
  /** System projects are hidden from the regular project list and back features like
   *  Quick Notes. They are created on demand and owned by the app, not the user. */
  isSystem?: boolean
  /** Identifier for the kind of system project (e.g. "quick-notes"). */
  systemKind?: string | null
}

// Goal model — a statement of direction for one life space.
// A Goal is NOT a task: it is never completed, checked off, or measured.
// It only expresses what currently matters to the user. See CONTEXT.md.
export interface Goal extends BaseEntity {
  title: string
  description: string | null // the optional "why"
  lifeSpace: LifeSpace
  userId: string | null // owner (Supabase auth user ID)
  /** Server-assigned cloud row id (PK). Used by sync push to target updates/
   *  deletes; `id` (= local_id) is the stable cross-device identity. */
  cloudId: string | null
  deletedAt: Date | null // soft delete
}

// Sync queue item for offline change tracking
export interface SyncQueueItem {
  id?: number // Auto-increment
  action: 'create' | 'update' | 'delete'
  table: 'tasks' | 'settings' | 'projects' | 'sections' | 'tags' | 'goals'
  recordId: number
  data: any
  synced: boolean
  timestamp: Date
  error?: string
  retryCount?: number
}

// Sync metadata for managing sync state
export interface SyncMetadata extends BaseEntity {
  tableName: string
  entityId: string
  localVersion: number
  remoteVersion: number | null
  conflictStatus: 'none' | 'local_newer' | 'remote_newer' | 'conflict'
  lastSyncAttempt: Date | null
}

// Type for creating new entities (without generated fields)
export type CreateTask = Omit<Task, keyof BaseEntity | 'order' | 'projectId' | 'sectionId' | 'userId' | 'cloudId' | 'deletedAt' | 'durationMode' | 'startDate' | 'startTime' | 'startPeriod' | 'dueTime' | 'duePeriod'> & {
  order?: number
  projectId?: string | null
  sectionId?: string | null
  durationMode?: 'duration' | 'thinking' | null
  startDate?: Date | null
  startTime?: string | null
  startPeriod?: StartPeriod | null
  dueTime?: string | null
  duePeriod?: StartPeriod | null
}
// `lifeSpace` stays required (not Omit'd) — every task must carry it.

export type CreateSetting = Omit<Setting, keyof BaseEntity>
export type CreateUserProfile = Omit<UserProfile, keyof BaseEntity>
export type CreateTimeEntry = Omit<TimeEntry, keyof BaseEntity>
export type CreateTag = Omit<Tag, keyof BaseEntity | 'usageCount' | 'userId' | 'cloudId' | 'deletedAt'> & {
  usageCount?: number
}
export type CreateProject = Omit<Project, keyof BaseEntity | 'order' | 'userId' | 'cloudId' | 'deletedAt'> & {
  order?: number
  userId?: string | null
}
export type CreateSection = Omit<Section, keyof BaseEntity | 'order' | 'userId' | 'cloudId' | 'deletedAt' | 'collapsed'> & {
  order?: number
  collapsed?: boolean
}
// `lifeSpace` and `title` stay required — every goal must carry them.
export type CreateGoal = Omit<Goal, keyof BaseEntity | 'userId' | 'cloudId' | 'deletedAt' | 'description'> & {
  userId?: string | null
  description?: string | null
}

// Type for updating entities (all fields optional except id)
export type UpdateTask = Partial<Omit<Task, 'id'>> & { id: string }
export type UpdateSetting = Partial<Omit<Setting, 'id'>> & { id: string }
export type UpdateUserProfile = Partial<Omit<UserProfile, 'id'>> & { id: string }
export type UpdateTimeEntry = Partial<Omit<TimeEntry, 'id'>> & { id: string }
export type UpdateTag = Partial<Omit<Tag, 'id'>> & { id: string }
export type UpdateProject = Partial<Omit<Project, 'id'>> & { id: string }
export type UpdateSection = Partial<Omit<Section, 'id'>> & { id: string }
export type UpdateGoal = Partial<Omit<Goal, 'id'>> & { id: string }

// Database schema version for migrations
export const DB_VERSION = 18 // Add goals table (Ziele feature)
export const DB_NAME = 'happytask_db'

// Default values
export const DEFAULT_TASK_ATTENTION: AttentionLevel | null = null
export const DEFAULT_TASK_STATUS: TaskStatus = 'todo'
export const DEFAULT_TASK_CATEGORY: TaskCategory = 'other'

export const DEFAULT_USER_PREFERENCES: UserProfile['preferences'] = {
  theme: 'light', // Default to light mode (dark mode not yet implemented)
  language: 'de',
  notifications: true,
  sounds: true,
  autoSync: false,
  workingHours: {
    start: '09:00',
    end: '17:00'
  },
  pomodoroSettings: {
    workDuration: 25,
    shortBreakDuration: 5,
    longBreakDuration: 15,
    cyclesBeforeLongBreak: 4
  }
}

export const DEFAULT_USER_STATISTICS: UserProfile['statistics'] = {
  totalTasksCompleted: 0,
  totalTimeSpent: 0,
  currentStreak: 0,
  longestStreak: 0,
  lastActiveDate: null
}